import esriConfig from '@arcgis/core/config';
import { BASEMAPS, COORD_TYPES, MAP_LAYER_IDS, MAP_MODES, MAP_TOOLS } from '../../lib/constants';
import { getPhoto } from '../../services/udapi/photos';
import { getUser } from '../../lib/user';
import * as mapViewEffects from './mapViewEffects';
// import { getTerrain } from '../../lib/map';

/*
  #TODO: Set up a different widget model, and move reducers/effects
  for widgets there, to not pollute the map one.
  -- TJC - June 18, 2021
*/

/*
  #TODO: Remove the select and new logic from this model. This model should only
  be concerned with things that the map view cares about that it should have
  quick access to that can't be easily derived from the main models.
  -- TJC - March 20, 2020
*/
/*
  #TODO: The selected waypoint field in the model should simply be an ID, and we
  can use that to reference the waypoint in the model itself.
  -- TJC - March 22, 2020
*/
/*
  #TODO: What TJ said above + also move the isFeatureDirty outside, possibly into features, as this
  model should be about map only.
  -- KD - April 22, 2020
*/
export default {
  namespace: 'map',
  state: {
		activeTool: null,
		basemapMode: BASEMAPS.Satellite,
		basemaps: [],
		isChangingMapMode: false,
		isMapLoading: false,
		isMapReady: false,
    isChangingBasemap: false,
		isUpdatingLocation: false,
		mapMode: MAP_MODES.TwoD,
		mapModeBeforeSelectionTool: MAP_MODES.TwoD,
		mapParams: { coordsStr: '', coordsType: COORD_TYPES.iso6709, tilt: null, zoom: null },
		mapRef: null,
		mapViewRef: null,
		measureTool: null, // remove measureTool, replace with activeTool?\
		sceneError: null,
		sceneViewRef: null,
		showGrid: true,
		sketchViewModel: null,
    // [WIP]: Terrain saved in state
		/* terrain: {
			threeD: {
				data: null,
				expires: null,
			},
		}, */
		widgetRefs: { elevationProfile: null, measurement: null, selectSketch: null, sketch: null },
		zoomLevel: null,
		/*
      #TODO: Move to features model.
      -- TJC - February 23, 2021
    */
		featureErrorId: '',
		hasFeatureErrored: false,
		isFeatureDirty: false,
		newFeature: null,
		selectedFeature: null,
	},
  effects: {
    ...mapViewEffects,

    *initBasemap({ payload }, { select, put }) {
      const basemaps = payload;
      let defaultBasemap = basemaps.find((bm) => bm.default == true);
      if (defaultBasemap == null) defaultBasemap = basemaps[0];

      yield put({ type: 'setBasemapList', payload: basemaps });
      yield put({ type: 'setSelectedBasemap', payload: defaultBasemap.name });
    },
    *resetWidgetState({ payload }, { select, put }) {
      let { activeTool } = yield select((model) => model.map);

      yield put({ type: 'cancelActiveTool' });
      yield put({ type: 'clearMeasurements' });
      if (activeTool === MAP_TOOLS.Select) {
        yield put({ type: 'features/resetAllSelectedFeatures' });
        yield put({ type: 'features/resetMapSelectList' });
        yield put({ type: 'clearLayer', payload: MAP_LAYER_IDS.select });
      }
    },
    // [WIP]: Gets terrain and stores it into state
    // *getTerrain({ payload: type }, { call, put, select }) {
    //   const { mapPreferences } = yield select((model) => model.user.profile)
    //   if (mapPreferences.elevationExaggeration) {
    //     const data = yield call(getTerrain, mapPreferences.elevationExaggeration)
    //     yield put({ type: 'setTerrain', payload: { data, type } })
    //   }
    // },
    *updateWaypointLocation({ payload }, { select, put }) {
      let feature = yield select((model) => model.features.selectedFeature);

      feature.geometry.coordinates[0] = payload.longitude;
      feature.geometry.coordinates[1] = payload.latitude;

      yield put({ type: 'updateFeature', payload: feature });
      yield put({ type: 'features/updateFeature', payload: feature });
    },
    *updateInterceptor(action, { call, select, put }) {
      const config = esriConfig;
      /* eslint-disable */
      const user = getUser();
      /* eslint-enable */

      const intIdx = config.request.interceptors.findIndex(
        (int) => int.id == 'maps'
      );
      if (intIdx > -1) {
        config.request.interceptors[intIdx].headers = {
          Authorization: `Bearer ${user.tokens.identity.value}`,
        };
      }

      const intIdx2 = config.request.interceptors.findIndex(
        (int) => int.id == 'proxy'
      );
      if (intIdx2 > -1) {
        config.request.interceptors[intIdx2].headers = {
          Authorization: `Bearer ${user.tokens.identity.value}`,
        };
      }

      yield null;
    },
    /*
      #TODO: Move to feature model
      -- TJC - July 14, 2021
    */
    *loadPhotosDataEffect({ _ }, { call, select, put }) {
      const selectedFeature = yield select(
        (model) => model.features.selectedFeature
      );

      if (selectedFeature) {
        const photos = yield call(() => {
          let photos = [];

          const promises = selectedFeature.photoIds.map((id) => {
            return getPhoto(id);
          });

          return Promise.all(promises)
            .then((results) => {
              results.forEach((item) => {
                if (item.success) {
                  photos.push(item.data);
                }
              });

              return photos;
            })
            .catch((err) => {              
              return [];
            });
        });

        selectedFeature.photos = photos;
        yield put({
          type: 'setSelectedFeature',
          payload: { ...selectedFeature },
        });
        // update featuresList as well
      }
    },
    *updateZoomLevel({ payload }, { select, put }) {
      const { mapViewRef, sceneViewRef, mapMode } = yield select(model => model.map)
      if (mapMode === MAP_MODES.TwoD) {
        mapViewRef.zoom = payload
        yield put({ type: 'initializeRefs', payload: { mapViewRef } });
      } else if (mapMode === MAP_MODES.ThreeD) {
        sceneViewRef.zoom = payload
        yield put({ type: 'initializeRefs', payload: { sceneViewRef } });
      }
    },
  },
  reducers: {
    initializeRefs(state, { payload }) {
      return {
        ...state,
        mapRef: payload.mapRef || state.mapRef,
        mapViewRef: payload.mapViewRef || state.mapViewRef,
        sceneViewRef: payload.sceneViewRef || state.sceneViewRef,
      };
    },
    setSceneError(state, { payload: error }) {
      return {
        ...state,
        sceneError: error,
      };
    },
    setMapReady(state, { payload }) {
      return {
        ...state,
        isMapReady: payload,
      };
    },
    setMapLoading(state, { payload }) {
      return {
        ...state,
        isMapLoading: payload,
      };
    },
    setIsChangingMapMode(state, { payload }) {
      return {
        ...state,
        isChangingMapMode: payload || !state.isChangingMapMode 
      };
    },
    setIsChangingBasemap(state, { payload }) {
      return {
        ...state,
        isChangingBasemap: payload || !state.isChangingBasemap 
      };
    },
    toggleShowGrid(state, action) {
      return {
        ...state,
        showGrid: !state.showGrid,
      };
    },
    setActiveTool(state, { payload: tool }) {
      return {
        ...state,
        activeTool: tool,
      };
    },
    setActiveMeasureTool(state, { payload: tool }) {
      return {
        ...state,
        measureTool: tool,
      };
    },
    setMeasurementWidgetRef(state, { payload: ref }) {
      return {
        ...state,
        widgetRefs: {
          ...state.widgetRefs,
          measurement: ref,
        },
      };
    },
    setSelectWidgetRef(state, { payload: ref }) {
      return {
        ...state,
        widgetRefs: {
          ...state.widgetRefs,
          selectSketch: ref,
        },
      };
    },
    setSketchWidgetRef(state, { payload: ref }) {
      return {
        ...state,
        widgetRefs: {
          ...state.widgetRefs,
          sketch: ref,
        },
      };
    },
    setElevProfWidgetRef(state, { payload: ref }) {
      return {
        ...state,
        widgetRefs: {
          ...state.widgetRefs,
          elevationProfile: ref,
        },
      };
    },
    setMapRef(state, { payload: map }) {
      return {
        ...state,
        mapRef: map,
      };
    },
    setViewRef(state, { payload: view }) {
      return {
        ...state,
        viewRef: view,
      };
    },
    setWidgetRef(state, { payload: widgets }) {
      return {
        ...state,
        widgetRefs: {
          ...state.widgetRefs,
          ...widgets,
        },
      };
    },
    setSelectedFeature(state, { payload: feature }) {
      return {
        ...state,
        selectedFeature: feature,
      };
    },
    saveMapModeBeforeSelectionTool(state) {
      return {
        ...state,
        mapModeBeforeSelectionTool: state.mapMode,
      };
    },
    setMapMode(state, { payload: mapMode }) {
      return {
        ...state,
        isChangingMapMode: true,
        mapMode,
      };
    },
    setSelectedBasemap(state, { payload: basemap }) {
      return {
        ...state,
        basemapMode: basemap,
      };
    },
    setBasemapList(state, { payload: basemaps }) {
      return {
        ...state,
        basemaps,
      };
    },
    setMapParams(state, { payload }) {
      return {
        ...state,
        mapParams: {
          ...state.mapParams,
          ...payload,
        },
      };
    },
    setSketchModel(state, { payload }) {
      return {
        ...state,
        sketchViewModel: payload,
      };
    },
    setIsUpdatingLocation(state, { payload }) {
      return {
        ...state,
        isUpdatingLocation: payload,
      };
    },
    setZoomLevel(state, { payload }) {
      return {
        ...state,
        zoomLevel: payload,
      };
    },
    // [WIP]: stores terrain in state
    /* setTerrain(state, { payload:{ data, type } }) {
      const { terrain } = state
      if (type === '3D') {
        terrain.threeD = {
          expires: Math.ceil(Date.now() / 1000) + (60 * 60 * 24),
          data
        }
      }
      return {
        ...state,
        terrain
      };
    }, */
  },
};
